import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AIUsageBar from '../components/AIUsageBar';
import { Lightbulb, Sparkles, Lock, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ActivityPlanner() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  const [topic, setTopic] = useState('');
  const [resources, setResources] = useState('');
  const [activities, setActivities] = useState([]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      
      // Check if user has Plus, Pro, or Parent tier
      if (!['plus', 'pro', 'parent'].includes(response.data.subscription_tier)) {
        toast.error('Activity Planner requires Teacher Plus, Pro, or Parent membership');
      }
    } catch (error) {
      navigate('/login?redirect=/ai-tools/activity-planner');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    setGenerating(true);
    setActivities([]);

    try {
      const response = await axios.post(
        `${API}/ai/suggest-activities`,
        {
          topic: topic.trim(),
          materials: resources.trim() || '',  // Backend expects 'materials' field
          resources: '',  // Keep for backward compatibility
          age: 8,  // Default age
          duration: 30  // Default duration in minutes
        },
        { withCredentials: true }
      );

      if (response.data && response.data.activities) {
        setActivities(response.data.activities);
        toast.success('Activities generated successfully!');
      } else {
        console.error('Unexpected response format:', response.data);
        toast.error('Generated activities but in unexpected format');
      }
      
      // Refresh user data to update usage
      checkAuth();
    } catch (error) {
      console.error('Error generating activities:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Plus tier required');
      } else if (error.response?.status === 429) {
        toast.error('Please wait before generating again');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate activities');
      }
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="py-24 px-6 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show locked screen for Free users only
  if (user && !['plus', 'pro', 'parent'].includes(user.subscription_tier)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <section className="py-16 px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-3xl border-2 border-slate-200 shadow-lg p-12">
              <div className="bg-yellow-100 rounded-full p-6 inline-block mb-6">
                <Lock className="w-16 h-16 text-yellow-600" />
              </div>
              
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                AI Activity Planner
              </h1>
              
              <p className="text-lg text-slate-600 mb-8">
                This feature is available for <strong>Teacher Plus</strong> and <strong>Teacher Pro</strong> members
              </p>

              <div className="bg-purple-50 rounded-2xl p-6 mb-8 border border-purple-200">
                <h3 className="font-bold text-purple-900 mb-3">What You'll Get:</h3>
                <ul className="text-left text-slate-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span>Get 3 hands-on activity ideas for any topic</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span>Uses only household/classroom items you already have</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span>No need to purchase materials</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span>Perfect for budget-conscious teaching</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={() => navigate('/pricing')}
                className="bg-purple-600 text-white hover:bg-purple-700 transition-all rounded-xl px-8 py-4 font-bold shadow-lg inline-flex items-center gap-2"
              >
                Upgrade to Plus or Pro
              </button>

              <button
                onClick={() => navigate('/ai-tools')}
                className="ml-4 text-slate-600 hover:text-slate-900 transition-colors inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to AI Tools
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/ai-tools')}
              className="text-purple-600 hover:text-purple-800 transition-colors inline-flex items-center gap-2 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to AI Tools
            </button>
            
            <h1 className="text-4xl font-bold text-slate-900 mb-2 flex items-center gap-3">
              <Lightbulb className="w-10 h-10 text-yellow-500" />
              AI Activity Planner
            </h1>
            <p className="text-base text-slate-600">
              Get creative, hands-on activity ideas using everyday items (Teacher Plus & Pro)
            </p>
          </div>

          {/* Usage Bar */}
          {user && (
            <AIUsageBar
              used={user.ai_generations_used || 0}
              limit={user.subscription_tier === 'pro' ? 9999 : user.subscription_tier === 'plus' ? 200 : 5}
              tier={user.subscription_tier || 'free'}
            />
          )}

          {/* Input Form */}
          <div className="bg-white rounded-3xl border-2 border-slate-200 shadow-lg p-8 mb-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  What topic do you want to teach? *
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., Fractions, Photosynthesis, Verb Tenses"
                  className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  What simple resources do you have available?
                </label>
                <input
                  type="text"
                  value={resources}
                  onChange={(e) => setResources(e.target.value)}
                  placeholder="e.g., buttons, paper, pencils, or type 'nothing'"
                  className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                />
                <p className="text-sm text-slate-500 mt-2">
                  Leave blank or type "nothing" if you want activities with no materials
                </p>
              </div>

              <button
                onClick={handleGenerate}
                disabled={generating || !topic.trim()}
                data-testid="suggest-activities-button"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 transition-all rounded-xl px-8 py-4 font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Generating Activities...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Suggest Activities
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Activities Display */}
          {activities && activities.length > 0 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900">Suggested Activities</h2>
              
              {activities.map((activity, index) => (
                <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border-2 border-blue-200 p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{activity.title || 'Activity'}</h3>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-bold text-slate-700 mb-1">Materials Needed:</p>
                          <p className="text-sm text-slate-600">{activity.materials || 'None specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-700 mb-1">Duration:</p>
                          <p className="text-sm text-slate-600">{activity.duration || 'Not specified'}</p>
                        </div>
                      </div>

                      {activity.steps && activity.steps.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-bold text-slate-700 mb-2">Steps:</p>
                          <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600">
                            {activity.steps.map((step, i) => (
                              <li key={i}>
                                {typeof step === 'string' 
                                  ? step 
                                  : `${step.instruction || step.description || JSON.stringify(step)}`}
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {activity.learning_outcome && (
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <p className="text-sm font-bold text-purple-900 mb-1">Learning Outcome:</p>
                          <p className="text-sm text-slate-700">{activity.learning_outcome}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ActivityPlanner;
